import { uploadAsset } from "../api.js";

/**
 * Dropping and pasting files into a page, shared by both editing modes so they
 * behave identically.
 *
 * The rule for what gets written is deliberately simple:
 *   - an image → `![name](./assets/name.png)`
 *   - anything else → `[name](./assets/report.pdf)`
 *
 * i.e. **an attachment is just a link.** No attachment node, no sidecar
 * metadata, no registry entry — so attachments survive markdown mode, GitHub,
 * a plain checkout, and an agent editing the file directly. Anything richer
 * (previews, sizes) can be layered on later as an embed kind without changing
 * what is written to the file.
 *
 * The link stays relative on disk; only the render rewrites it to /api/blob
 * (imageSrc in markdown/Markdown.tsx). That split is datawiki's existing model
 * and it survived contact with an off-the-shelf editor intact.
 */

/** The markdown for an uploaded file. Images embed; everything else links. */
export function markdownFor(name: string, url: string, isImage: boolean): string {
  const label = name || "file";
  return isImage ? `![${label}](${url})` : `[${label}](${url})`;
}

/** Files carried by a drag or paste event, if any. */
export function filesFrom(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const files = [...(dt.files ?? [])];
  if (files.length) return files;
  // A pasted screenshot arrives as an item, not in the file list.
  return [...(dt.items ?? [])]
    .filter((i) => i.kind === "file")
    .map((i) => i.getAsFile())
    .filter((f): f is File => f !== null);
}

/**
 * Upload every file and return the markdown to insert, or "" if none landed.
 * A failed upload is reported through `onError` and simply contributes
 * nothing — the document is never left holding a link to a file that isn't
 * there.
 */
export async function uploadAll(
  files: File[],
  repo: string,
  pagePath: string,
  onError?: (message: string) => void,
): Promise<string> {
  const parts: string[] = [];
  for (const file of files) {
    try {
      const up = await uploadAsset(repo, file, pagePath);
      parts.push(markdownFor(file.name, up.url, up.isImage));
    } catch (e) {
      onError?.(e instanceof Error ? e.message : String(e));
    }
  }
  return parts.join("\n\n");
}
